/**
 * Tiny fetch wrapper for the Angadi API (docs/API.md). No axios — keeps the bundle small.
 * Shoppers never authenticate, so no token handling here (the owner app has its own client).
 * Throws ApiError with the backend's error envelope: {"error": {code, message, details}}.
 */
import { env } from './env'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, message: string, details: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

type Query = Record<string, string | number | boolean | null | undefined>

export function buildUrl(path: string, query?: Query): string {
  const url = `${env.apiBaseUrl}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  query?: Query
  body?: unknown
  signal?: AbortSignal
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  // Demo build (D-76): answers come from the bundled sample data. The flag is replaced at build
  // time, so the normal build drops this branch and never ships the demo data.
  if (import.meta.env.VITE_DEMO === '1') {
    const { demoApi, DemoNotFound } = await import('@/demo/api')
    try {
      return demoApi(path, opts) as T
    } catch (e) {
      if (e instanceof DemoNotFound) throw new ApiError(404, 'not_found', e.message, null)
      throw e
    }
  }
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })

  if (res.status === 204) return undefined as T
  const data: unknown = await res.json().catch(() => null)
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } } | null)
      ?.error
    throw new ApiError(
      res.status,
      err?.code ?? 'error',
      err?.message ?? res.statusText,
      err?.details,
    )
  }
  return data as T
}

/**
 * Fire-and-forget write that survives page navigation (logging, right before opening WhatsApp).
 * The body is JSON sent as `text/plain`: a beacon cannot send `application/json` cross-origin and
 * text/plain needs no CORS preflight; the API accepts it on its logging endpoints (API.md).
 * Never throws (BR-13).
 */
export function beacon(path: string, body: unknown): void {
  if (import.meta.env.VITE_DEMO === '1') return // the demo logs nothing
  try {
    const payload = JSON.stringify(body)
    const url = buildUrl(path)
    const blob = new Blob([payload], { type: 'text/plain' })
    if (typeof navigator !== 'undefined' && navigator.sendBeacon?.(url, blob)) return
    void fetch(url, {
      method: 'POST',
      body: payload,
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
    }).catch(() => undefined)
  } catch {
    /* logging never affects the user */
  }
}
